<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import type { Option } from '@/types/app'
import {
  stringifyUserVariableDataType,
  type UserVariableDefinitionValueTypeName,
} from '@/features/userVariable/dataType'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { nanoid } from 'nanoid'
import * as v from 'valibot'
import { formSchemaRecord } from './schema'
import { useCreateUserVariableDefinitionMutation } from './SettingsPageLineActivationUserVariableCreateView.generated'
import {
  UserVariableDefinitionDataType,
  type CreateUserVariableDefinitionInput,
} from '@/types/schema.generated'
import { useAlert } from '@/libs/alertPortal'
import { useRoute, useRouter } from 'vue-router'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'

const alert = useAlert()
const route = useRoute('LineActivationSettingUserVariableCreateView')
const router = useRouter()

type SelectOption = {
  key: string
  value: string
}

const form = ref<{
  name: string
  dataType: UserVariableDefinitionValueTypeName
  selectOptions: SelectOption[]
}>({
  name: '',
  dataType: 'UserVariableDefinitionValueString',
  selectOptions: [
    {
      key: nanoid(),
      value: '',
    },
  ],
})

const dataTypeOptions: Option<UserVariableDefinitionValueTypeName>[] = [
  {
    value: 'UserVariableDefinitionValueString',
    text: stringifyUserVariableDataType('UserVariableDefinitionValueString'),
  },
  {
    value: 'UserVariableDefinitionValueText',
    text: stringifyUserVariableDataType('UserVariableDefinitionValueText'),
  },
  {
    value: 'UserVariableDefinitionValueSelect',
    text: stringifyUserVariableDataType('UserVariableDefinitionValueSelect'),
  },
  {
    value: 'UserVariableDefinitionValueMultipleSelect',
    text: stringifyUserVariableDataType(
      'UserVariableDefinitionValueMultipleSelect'
    ),
  },
]

const emptyErrors: Record<string, string | undefined> = {
  name: undefined,
  selectOptions: undefined,
}

const errors = ref({ ...emptyErrors })

const handleClickAddSelectOptionButton = () => {
  form.value.selectOptions = [
    ...form.value.selectOptions,
    {
      key: nanoid(),
      value: '',
    },
  ]
}

const createMutation = useCreateUserVariableDefinitionMutation()

const isClickedSubmitButtonOnce = ref(false)

const handleClickSubmitButton = async () => {
  isClickedSubmitButtonOnce.value = true

  // バリデーションする

  if (!validate()) {
    return
  }

  const input = ((): CreateUserVariableDefinitionInput => {
    switch (form.value.dataType) {
      case 'UserVariableDefinitionValueString':
        return {
          name: form.value.name,
          dataType: UserVariableDefinitionDataType.String,
        }
      case 'UserVariableDefinitionValueText':
        return {
          name: form.value.name,
          dataType: UserVariableDefinitionDataType.Text,
        }
      case 'UserVariableDefinitionValueSelect':
        return {
          name: form.value.name,
          dataType: UserVariableDefinitionDataType.Select,
          selectOptions: form.value.selectOptions
            .map(option => option.value)
            .filter(s => s !== ''),
        }
      case 'UserVariableDefinitionValueMultipleSelect':
        return {
          name: form.value.name,
          dataType: UserVariableDefinitionDataType.MultipleSelect,
          selectOptions: form.value.selectOptions
            .map(option => option.value)
            .filter(s => s !== ''),
        }
    }
  })()

  // リクエストする
  await createMutation.mutate({
    input,
  })

  alert.success('カスタム項目を作成しました')

  // 一覧にリダイレクトする
  // eslint-disable-next-line fp/no-mutating-methods
  router.push({
    name: 'LineActivationSettingUserVariableListView',
  })
}

const validate = (): boolean => {
  errors.value = { ...emptyErrors }

  const schema = formSchemaRecord[form.value.dataType]

  const result = v.safeParse(schema, form.value)
  if (result.success) {
    return true
  } else {
    result.issues.forEach(issue => {
      const path = v.getDotPath(issue)
      if (!path) {
        return
      }

      errors.value[path] = issue.message
    })
    return false
  }
}

const hasError = computed(() => {
  return Object.values(errors.value).some(e => e != null)
})

watch(form.value, () => {
  if (isClickedSubmitButtonOnce.value) {
    validate()
  }
})
</script>

<template>
  <div>
    <BreadcrumbList :links="route.meta.breadcrumbs" />
    <SettingsPagePanel :class="$style.panel">
      <SettingsPagePanelHeader>
        <template #title>カスタム項目 新規作成</template>
      </SettingsPagePanelHeader>
      <div :class="$style.form">
        <div :class="$style.formRow">
          <p :class="$style.label">名前</p>
          <DolphinTextInput
            :class="$style.textInput"
            v-model="form.name"
            :invalid="!!errors.name"
          />
          <p v-if="errors.name" :class="$style.error">{{ errors.name }}</p>
        </div>
        <div :class="$style.formRow">
          <p :class="$style.label">データ型</p>
          <DolphinSelector
            :options="dataTypeOptions"
            v-model="form.dataType"
            :class="$style.dataTypeSelector"
          />
          <div
            v-if="
              form.dataType === 'UserVariableDefinitionValueSelect' ||
              form.dataType === 'UserVariableDefinitionValueMultipleSelect'
            "
          >
            <p :class="$style.selectorHeading">選択肢</p>
            <div :class="$style.selectOptions">
              <div
                v-for="selectOption in form.selectOptions"
                :key="selectOption.key"
              >
                <DolphinTextInput
                  v-model="selectOption.value"
                  placeholder="選択肢を入力..."
                  :class="$style.selectOptionInput"
                />
              </div>
            </div>
            <button
              @click="handleClickAddSelectOptionButton"
              :class="$style.addSelectOptionButton"
            >
              選択肢を追加する
            </button>
            <p v-if="errors.selectOptions" :class="$style.error">
              {{ errors.selectOptions }}
            </p>
          </div>
        </div>
        <div :class="$style.submit">
          <FlatButton
            @click="handleClickSubmitButton"
            :disabled="hasError"
            variant="primary"
            :class="$style.submit"
            >この内容で保存する</FlatButton
          >
        </div>
      </div>
    </SettingsPagePanel>
  </div>
</template>

<style module lang="scss">
.panel {
  margin-top: 16px;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.formRow {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.error {
  color: $color-caution-accent;
  font-weight: 700;
}

.label {
  color: $color-text-and-object-high-emphasis;
  font-size: 20px;
  font-weight: bold;
  line-height: 24px;
}

.textInput {
  width: 100%;
}

.dataTypeSelector {
  width: 300px;
}

.selectorHeading {
  font-size: 16px;
  font-weight: bold;
}

.selectOptions {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selectOptionInput {
  width: 360px;
}

.addSelectOptionButton {
  margin-top: 8px;
  text-decoration: underline;
}

.submit {
  display: flex;
  margin-top: 16px;
  justify-content: center;
}
</style>
